import _ from 'lodash';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import {
  DialogContent,
  DialogActions,
  FormHelperText,
  Grid as MuiGrid,
} from '@mui/material';
import { withStyles } from '@mui/styles';

// lib
// platform helpers
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { withShowable } from '../../console/_global/lib/withShowable';
import { AgencyTeamsAccountCreationHeader } from '../../accounts/admin-account-flow/AgencyTeamsAccountCreationHeader';
import { PubSub } from '../../utils/eventUtils';
// components
import withRouter from '../hocs/withRouter';
import CBAutocomplete from '../autocomplete/CBAutocomplete';

import CBButton from '../Buttons/CbButton';

// actions
import {
  addNewAccount,
  getAutocompleteAccounts,
} from '../../accounts/AccountService';

import { getIsUsPlatform, manageAPIError } from '../../utils';
import { withFormController } from '../hocs/forms';
import LanguageForm, {
  formatPhoneAsString,
  useLanguageFormState,
} from '../../i18n/forms/LanguageForm';
import { FormLanguageProvider } from '../../i18n/forms/FormLanguageProvider';
import TextFieldBase from '../inputs/text-fields/base/TextFieldBase';
import { LANGUAGE_COUNTRY_MAPPING } from '../../i18n/i18n.language-config';
import { queries } from '../../queries';
import { createSubmission } from '../../api/submissions/submissions.api';
import { buildCreateSubmissionPayload } from '../../accounts/create-account-flow/helpers';
import { usePersona } from '../hooks/usePersona';
import { useActor } from '../hooks/useActor';
import { FEATURE_FLAG_KEYS } from '../../config/growthbook';

const TextField = withFormController(TextFieldBase);
const Grid = withShowable(MuiGrid);

const defaultValues = {
  state: '',
  accountName: '',
  phone: '',
  street: '',
  city: '',
  zipCode: '',
};

let searchStr = '';
const AccountsCreateEdit = withRouter(
  ({ classes, close, location, ...props }: any) => {
    const { enqueueSnackbar } = useSnackbar();
    const teams = useSelector(
      ({ uiSettings }) => uiSettings.team.accountCreation
    );

    const { gridLayoutSettings, ...languageFormState } = useLanguageFormState();
    const isUSPlatform = getIsUsPlatform(languageFormState.language);

    const route = location.pathname.split('/')[1];
    const queryClient = useQueryClient();
    const persona = usePersona();
    const actor = useActor();
    const history = useHistory();
    const isSubmissionsEnabled = useFeatureIsOn(
      FEATURE_FLAG_KEYS.SUBMISSIONS_VIEW
    );

    const { handleSubmit, ...methods } = useForm({
      defaultValues,
    });

    const {
      control,
      getValues,
      formState: { isSubmitting, errors },
      setValue,
    } = methods;

    const values = getValues();

    const [state, setState] = React.useState({
      cbid: null,
      accountNames: [],
      isSearching: true,
      accountName: '',
      errorMessage: '',
    });

    const resetHandler = () => {
      setState((prevState) => {
        return {
          ...prevState,
          cbid: null,
          accountNames: [],
          isSearching: true,
          accountName: '',
          errorMessage: '',
        };
      });

      methods.reset(defaultValues);

      PubSub.publish('updateButtonText', {
        buttonId: 'AccountsCreateEdit-submit',
        buttonText: 'Search',
      });
    };

    const accountNamesSearch = (addressState: any) => {
      if (searchStr) {
        const SEARCH_PARAMS = isUSPlatform
          ? {
              state: addressState,
              search: searchStr,
              page: 0,
              pageSize: 10,
            }
          : {
              country:
                LANGUAGE_COUNTRY_MAPPING[
                  languageFormState.language as keyof typeof LANGUAGE_COUNTRY_MAPPING
                ],
              search: searchStr,
              page: 0,
              pageSize: 10,
            };
        getAutocompleteAccounts(SEARCH_PARAMS).then((resp: any) => {
          const accountNames = resp.data.content || [];

          setState((prevState) => {
            return {
              ...prevState,
              accountNames,
            };
          });
        });
      }
    };

    const searchAccountsByName = useCallback(
      _.debounce(accountNamesSearch, 200),
      []
    );

    const onSubmit = async (formData: any) => {
      const payload = buildPayload(
        formData,
        state,
        languageFormState,
        teams,
        persona,
        actor,
        isSubmissionsEnabled
      );

      if (state.isSearching) {
        close();
        props.history.push(`/${route}/manage/account/lookup`, {
          state: formData.state,
          accountName: formData.accountName,
          preload: true,
          city: formData.city,
          zip: formData.zipCode,
          phone: formData.phone,
          street: formData.street,
          teams: { label: teams.name, value: teams.id },
          region: languageFormState.language,
        });
      } else {
        try {
          await languageFormState.validateAddress({
            address1: formData.street,
            city: formData.city,
            zipCode: formData.zipCode,
          });

          if (isSubmissionsEnabled) {
            // @ts-expect-error TS(2322): Type '{ cbid: any; firmographics: { phone...
            return createSubmission({ data: payload })
              .then((resp) => {
                close();
                enqueueSnackbar('Account Created Successfully!', {
                  variant: 'success',
                });

                queryClient.invalidateQueries(queries.submissions.list._def);
                history.push(
                  `/agency/submissions?search=${resp.data.submissionNumber}`
                );
              })
              .catch((error) => {
                const errorMessage = manageAPIError(
                  error,
                  'Server Error. Please try again'
                );

                setState((prevState) => ({
                  ...prevState,
                  errorMessage,
                }));
              });
          }
          return addNewAccount(payload)
            .then(() => {
              close();
              enqueueSnackbar('Account Created Successfully!', {
                variant: 'success',
              });

              queryClient.invalidateQueries(queries.accounts.list._def);
            })
            .catch((error) => {
              const errorMessage = manageAPIError(
                error,
                'Server Error. Please try again'
              );

              // @ts-expect-error TS(2345): Argument of type '{ errorMessage: any; }' is not a... Remove this comment to see the full error message
              setState({
                errorMessage,
              });

              if (_.get(error, 'response.status', 409) === 409) {
                resetHandler();
                enqueueSnackbar(errorMessage, { variant: 'error' });
              }
            });
        } catch {
          //
        }
      }
    };

    const handleAccountNameChange = (event: any, value: any) => {
      if (value && typeof value === 'object') {
        PubSub.publish('updateButtonText', {
          buttonId: 'AccountsCreateEdit-submit',
          buttonText: 'Add Account',
        });

        setState((prevState) => {
          return {
            ...prevState,
            isSearching: false,
            cbid: value.cbid,
            accountName: value.name,
          };
        });

        setValue('accountName', value.name);
        setValue('phone', value.phoneNumber);
        setValue('street', value.address1);
        setValue('city', value.city);
        setValue('zipCode', value.zipCode);
        setValue('state', value.state);
      }
    };

    const handleSearch = (event: any) => {
      const { value } = event.target;
      setState((prevState) => {
        return {
          ...prevState,
          accountName: value,
        };
      });
      setValue('accountName', value);
      if (!value) {
        setState((prevState) => {
          return {
            ...prevState,
            accountNames: [],
          };
        });
        searchStr = '';
      }

      if (value) {
        searchStr = value;
        searchAccountsByName(values?.state ?? '');
      }
    };

    return (
      <section>
        <FormLanguageProvider language={languageFormState.language}>
          <FormProvider {...methods} handleSubmit={handleSubmit}>
            <form
              noValidate
              onSubmit={handleSubmit(onSubmit)}
              data-qa="newAccount"
            >
              <DialogContent classes={{ root: classes.container }}>
                <Grid container spacing={6}>
                  <Grid
                    item
                    md={route === 'agency' ? 6 : 12}
                    show={gridLayoutSettings.showState}
                  >
                    <LanguageForm.State
                      showFullStates
                      disabled={!state.isSearching}
                    />
                  </Grid>
                  <Grid
                    item
                    md={gridLayoutSettings.showState ? 6 : 12}
                    show={route === 'agency'}
                  >
                    <AgencyTeamsAccountCreationHeader data={state} />
                  </Grid>
                </Grid>

                <section className={classes.autocompleteContainer}>
                  <CBAutocomplete
                    id="accountName"
                    required="true"
                    inputValue={state.accountName || ''}
                    placeholder="please enter account name"
                    options={state.accountNames}
                    disabled={!state.isSearching}
                    getOptionLabel={(o: any) => o?.name ?? ''}
                    renderOption={renderAccountOption(
                      classes,
                      languageFormState.language
                    )}
                    onChangeHandler={handleAccountNameChange}
                    label="Account Name"
                    onTextChangeHandler={handleSearch}
                    data-qa="name"
                  />
                  {errors.accountName && (
                    <span role="alert" style={{ color: '#fa8072' }}>
                      Account Name is exceeding 100 Characters. Please reset
                      Handler.
                    </span>
                  )}
                </section>

                <Grid container spacing={2}>
                  <Grid item sm={12}>
                    <LanguageForm.PhoneField
                      name="phone"
                      fullWidth
                      required={false}
                      disabled={!state.isSearching}
                    />
                  </Grid>

                  <Grid item sm={12} style={{ paddingTop: 0 }}>
                    <TextField
                      placeholder="enter street address"
                      name="street"
                      label="Street"
                      // @ts-expect-error TS(2322): Type 'Control<{ state: string; accountName: string... Remove this comment to see the full error message
                      controllerProps={{ control }}
                      fullWidth
                      disabled={!state.isSearching}
                      data-qa="street"
                    />
                  </Grid>

                  <Grid item sm={6}>
                    <LanguageForm.City
                      required={false}
                      disabled={!state.isSearching}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <LanguageForm.PostalCode
                      required={false}
                      disabled={!state.isSearching}
                    />
                  </Grid>
                </Grid>
                <FormHelperText className="api-text-error">
                  {state.errorMessage}
                </FormHelperText>
                <section
                  className={`${classes.showWarning} ${
                    state.isSearching && values.accountName
                      ? ''
                      : classes.hideWarning
                  }`}
                >
                  <section className={classes.warning1}>
                    <img
                      src="images/info-2.svg"
                      className={classes.icon}
                      alt="Info icon"
                    />
                    Can’t find the new account? Just hit search
                  </section>
                </section>
              </DialogContent>
              <DialogActions>
                <CBButton action={close} styleName="cancel">
                  Cancel
                </CBButton>
                <CBButton
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting || !(values.accountName && teams.id)}
                  styleName="ctaButton"
                  buttonText="Search"
                  buttonId="AccountsCreateEdit-submit"
                  data-qa="search"
                />
              </DialogActions>
            </form>
          </FormProvider>
        </FormLanguageProvider>
      </section>
    );
  }
);

const styles = ({ palette, config }: any) => {
  return {
    container: {
      flex: '1 1 auto',
      padding: '0 5rem 3rem',
      overflowY: 'visible',
    },

    icon: {
      paddingLeft: 5,
      paddingRight: 5,
      width: '2rem',
    },
    warning1: {
      display: 'flex',
      justifyContent: 'center',
    },
    showWarning: {
      fontSize: '1.167rem',
      visibility: 'visible',
      lineHeight: 1.14,
      color: palette.primary.mainYellow,
      textAlign: 'center',
    },
    hideWarning: {
      visibility: 'hidden !important',
    },
    resetButton: {
      position: 'absolute',
      top: '90px',
      right: 0,
      '& .MuiButton-label': {
        fontSize: '1.167rem',
        fontWeight: 600,
      },
      lineHeight: 1.5,
      color: config.colors.cowbellBlue,
      cursor: 'pointer',
      paddingRight: '1rem',
    },
    resetDisabled: {
      color: palette.button.disabledBackground,
      cursor: 'auto',
    },
    autocompleteContainer: {
      marginTop: '1.5rem',
      marginBottom: '1rem',
    },
    domainName: {
      bottom: 10,
      right: 0,
      margin: '0 !important',
      fontSize: '0.67rem',
    },
    itemContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    itemNameLine: {
      margin: 0,
      fontWeight: 'bold',
      lineHeight: 1.5,
      color: palette.primary.main,
      fontSize: '1.167rem',
    },
    error: {
      textAlign: 'left',
      maxWidth: '600px',
      fontSize: config.textSizes.normal,
    },
    stateInput: {
      padding: 0,
    },
    addressLine: {
      margin: '0 !important',
      fontSize: '1rem',
      lineHeight: 1.17,
      color: palette.primary.secondary,
    },
  };
};

function renderAccountOption(classes: any, language: any) {
  return (optionProps: any, option: any) => {
    if (option.name) {
      const { domainName, address1, name, city, zipCode, phoneNumber } = option;
      const tempPhone = phoneNumber.replace(/[^0-9]/g, '');
      const finalPhone = formatPhoneAsString(language, tempPhone);

      const stateVal = option.state;
      return (
        <li {...optionProps}>
          <div className={classes.itemContainer}>
            <section>
              <p className={classes.itemNameLine}>{name}</p>
              <p className={classes.addressLine}>
                {address1}, {city}, {stateVal}, {zipCode}
              </p>
              <p className={classes.addressLine}>{finalPhone}</p>
            </section>
            <span className={classes.domainName}>{domainName}</span>
          </div>
        </li>
      );
    }

    return <li {...optionProps}>''</li>;
  };
}

// @ts-expect-error TS(2345): Argument of type '({ palette, config }: any) => { ... Remove this comment to see the full error message
export default withStyles(styles)(withRouter(AccountsCreateEdit));

const buildPayload = (
  formData: any,
  state: any,
  languageFormState: any,
  teams: any,
  persona: any,
  actor: any,
  isSubmissionsEnabled: boolean
) => {
  return isSubmissionsEnabled
    ? buildCreateSubmissionPayload(
        {
          ...formData,
          name: formData.accountName,
          address1: formData.street,
          cbid: state.cbid,
          phoneNumber: formData.phone,
          country:
            LANGUAGE_COUNTRY_MAPPING[
              languageFormState.language as keyof typeof LANGUAGE_COUNTRY_MAPPING
            ],
          team: {
            label: teams?.name ?? '',
            value: teams?.id ?? '',
          },
        },
        persona,
        actor
      )
    : {
        state: formData.state,
        name: formData.accountName,
        cbid: state.cbid,
        phone: formData.phone,
        address1: formData.street,
        city: formData.city,
        zipCode: formData.zipCode,
        teams: [{ name: _.get(teams, 'name', ''), id: _.get(teams, 'id', '') }],
        country:
          LANGUAGE_COUNTRY_MAPPING[
            languageFormState.language as keyof typeof LANGUAGE_COUNTRY_MAPPING
          ],
      };
};
